// TalkButton — L3 Talk in the composer (Douglas plan T20, D10).
//
// A live voice conversation with the chat the composer sends to: what the
// person says goes into the chat as a message (useLiveConversation's
// ask_agent), and the agent's replies are spoken. One button, in the
// composer's leading slot on the agent page, the start screen and a page
// helper. It is drawn disabled with a reason, never hidden, when voice is not
// available (GET /assistant/realtime/status: the customer's processing must
// stay in the UK, the AI access level, the Manager role) or when the host
// itself refuses the agent. While it is on, a caption beside it shows what was
// heard and what is being said.
//
// Switched off everywhere since 19 Oct 2026 (Adam: "hide the voice button
// completely for now. Just leave the mic"): TALK_ENABLED is false, so no host
// draws it and it asks the status route nothing. Voice models on Amazon are
// still to be tried in conversation; set it to true to bring Talk back.

import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import DisabledReason from '../../components/system/DisabledReason'
import { API_BASE_URL } from '../../constants/system'
import useLiveConversation, { type LiveConnect, type LiveConversationSession, type LiveState } from '../../voice/useLiveConversation'
import { PILL_RADIUS } from './surface.js'

export const VOICE_STATUS_CHECKING = 'Checking whether voice is available…'
export const VOICE_STATUS_UNKNOWN = 'Could not check whether voice is available. Try again in a moment.'
/** Talk is drawn only when this is true (off since 19 Oct 2026; see the header). */
export const TALK_ENABLED = false

/** Whether the caller may talk for this customer, and if not why (T18's status route). */
function useVoiceAvailability(customer: string): { allowed: boolean; reason?: string } {
  const [status, setStatus] = useState<{ customer: string; allowed: boolean; reason?: string } | null>(null)
  // Trigger: the customer. The request is dropped when it changes or the button goes.
  useEffect(() => {
    const controller = new AbortController()
    axios
      .get<{ allowed: boolean; reason?: string }>(`${API_BASE_URL}/assistant/realtime/status`, { params: { customer }, signal: controller.signal })
      .then((res) => setStatus({ customer, allowed: !!res.data?.allowed, reason: res.data?.reason || undefined }))
      .catch(() => { if (!controller.signal.aborted) setStatus({ customer, allowed: false, reason: VOICE_STATUS_UNKNOWN }) })
    return () => controller.abort()
  }, [customer])
  if (!status || status.customer !== customer) return { allowed: false, reason: VOICE_STATUS_CHECKING }
  return status.allowed ? { allowed: true } : { allowed: false, reason: status.reason || VOICE_STATUS_UNKNOWN }
}

const STATE_LABEL: Record<LiveState, string> = {
  off: 'Talk to the agent',
  connecting: 'Starting the voice…',
  listening: 'Listening. Click to stop talking',
  speaking: 'Speaking. Click to stop talking',
  waiting: 'The agent is working. Click to stop talking',
}

export interface TalkButtonProps {
  /** The chat the person talks to (its sendMessage is the one Send uses). */
  session: LiveConversationSession
  /** The customer: whose voice rules apply. */
  customer: string
  /** The host refuses the agent (AI access): the button says so. */
  disabledReason?: string
  /** Tests pass a fake connection. */
  connect?: LiveConnect
}

export default function TalkButton(props: TalkButtonProps & { enabled?: boolean }) {
  const { enabled = TALK_ENABLED, ...rest } = props
  return enabled ? <TalkButtonOn {...rest} /> : null
}

function TalkButtonOn({ session, customer, disabledReason, connect }: TalkButtonProps) {
  const availability = useVoiceAvailability(customer)
  const live = useLiveConversation(session, { customer, connect })
  const on = live.state !== 'off'
  // A live conversation can always be stopped, whatever the status says now.
  const reason = on ? undefined : disabledReason ?? availability.reason
  const title = live.error && !on ? `Talk stopped: ${live.error}` : STATE_LABEL[live.state]

  const button = (
    <IconButton
      size="small"
      aria-label={on ? 'Stop talking' : 'Talk to the agent'}
      aria-pressed={on}
      data-testid="talk-button"
      data-state={live.state}
      onClick={() => { if (on) live.stop(); else void live.start() }}
      sx={{
        width: 34, height: 34, flexShrink: 0, color: 'info.main',
        ...(on ? { bgcolor: 'info.main', color: 'info.contrastText', '&:hover': { bgcolor: 'info.dark' } } : {}),
      }}
    >
      {live.state === 'connecting'
        ? <CircularProgress size={18} color="inherit" />
        : <GraphicEqIcon sx={{ fontSize: 22 }} />}
    </IconButton>
  )

  return (
    <>
      {reason ? <DisabledReason reason={reason}>{button}</DisabledReason> : <Tooltip title={title}>{button}</Tooltip>}
      {on && live.caption && (
        <Box
          data-testid="talk-caption"
          aria-live="polite"
          sx={{
            maxWidth: 260, minWidth: 0, px: 1.25, py: 0.5, borderRadius: PILL_RADIUS,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
          }}
        >
          <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{live.caption.speaker === 'you' ? 'You' : 'Voice'}: </Box>
            {live.caption.text}
          </Typography>
        </Box>
      )}
    </>
  )
}
